import { allCapsOf, capLabel, type McpCatalog, type McpQuotaAll } from "../../api/mcp";
import {
  CouldNotLook,
  Denied,
  EmptyByDesign,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Blurred } from "../../components/Blurred";
import { Badge, Card, Notice, Page, Panel, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { Catalog } from "./Catalog";
import { useResource } from "../../hooks/useResource";
import { ProductScope } from "../../products/PerProduct";

/** Tier × tool cap matrix. Every tier links to its editor. */
export function Caps({ config }: { config: AppConfig }) {
  const { state, reload } = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // Owned HERE and handed to the catalog panel below. Two endpoints means two
  // requests either way — but one component owning both states means a cap
  // table cannot end up containing an unrelated "could not load the catalog"
  // failure, which read as though this page had half-broken.
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");

  return (
    <Page title="Tool caps" subtitle="Per-tier daily credit caps, by tool.">

      {/* WHOLLY MCP'S. A per-tool daily credit cap is an MCP concept — Labeeb
          meters messages and tokens against a tier envelope instead, and none
          of it is reachable. Capping every tool here does not slow the
          assistant down by one message. */}
      <ProductScope owns={["mcp"]}>
        Labeeb meters a tier envelope, not per-tool credits — nothing on this page limits it
      </ProductScope>

      {state.kind === "loading" && <Loading what="caps" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the cap table" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "notfound" && <LoadFailed what="caps" message="Route not found." />}
      {state.kind === "invalid" && (
        <LoadFailed what="caps" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="caps" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const q = state.data;
          const caps = allCapsOf(q);
          const tiers = Object.keys(caps).sort();

          return (
            <>
              <Notice title="Reading a cap">
                <span className="ref">-1</span> unlimited · <span className="ref">0</span>{" "}
                gated, never allowed · <span className="ref">N</span> metered, N credits a
                day resetting at 00:00 UTC. A tool with no row inherits the tier's{" "}
                <span className="ref">*</span> default.
              </Notice>

              {/* The resolver warning that used to live here is GONE — MCP
                  removed the resolver-cap guard entirely (RESOLVER_GUARD_REMOVED
                  in api/mcp.ts) and unprotected_resolvers with it. There is no
                  data left to compute "N unprotected resolvers" from. The
                  warning that matters now lives in the cap editor, at the
                  moment a search_* tool is actually being capped. */}

              {/* One CARD per tier, which is the shape every other panel in
                  this console has. These were bare sections with an `h2` and a
                  text link, so the tier tables sat directly on the page with
                  nothing bounding them and the row count read as part of the
                  heading rather than as a caption. */}
              {tiers.map((tier) => {
                const tierCaps = caps[tier] ?? {};
                const entries = Object.entries(tierCaps).sort(([a], [b]) =>
                  a === "*" ? -1 : b === "*" ? 1 : a.localeCompare(b),
                );

                return (
                  <Card
                    key={tier}
                    title={<span className="ref">{tier}</span>}
                    note={
                      <>
                        {entries.length} cap row{entries.length === 1 ? "" : "s"}
                      </>
                    }
                    aside={
                      <Button outline href={`/policy/caps/${encodeURIComponent(tier)}`}>
                        Open
                      </Button>
                    }
                  >
                    {entries.length === 0 ? (
                      <EmptyByDesign scope={`tier ${tier}`} />
                    ) : (
                      <Table
                        className="[--gutter:0px]"
                        head={
                          <tr>
                            <Th>Tool</Th>
                            <Th right>Cap</Th>
                          </tr>
                        }
                      >
                        {entries.map(([tool, n]) => {
                          const c = capLabel(n);
                          return (
                            <tr key={tool}>
                              <Td>
                                <span className="ref">{tool}</span>
                                {tool === "*" && (
                                  <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                                    default for every tool without a row
                                  </span>
                                )}
                              </Td>
                              <Td right>
                                <Badge tone={c.tone}>{c.text}</Badge>
                              </Td>
                            </tr>
                          );
                        })}
                      </Table>
                    )}
                  </Card>
                );
              })}

              {q.effective_within_seconds !== undefined && (
                <Notice>
                  Cap changes reach merchants within ~
                  {Math.round(q.effective_within_seconds / 60)} minutes — the cap map is
                  cached with a {q.effective_within_seconds}-second TTL and nothing
                  invalidates it across tasks.
                </Notice>
              )}
            </>
          );
        })()}
      {/* The design's "Promote alert-only → enforcing".
          MCP has no such mode: a cap is a number, and it is enforced from the
          moment it is written. There is no alert-only state to promote OUT of,
          so this is not a missing screen but a missing concept — which is why
          it is drawn rather than built. Shipping the button against today's API
          would enforce a cap the operator believed was only warning. */}
      <Card title="Rollout mode">
        <Blurred
          what="Write a cap as alert-only, then promote it to enforcing"
          needs="an alert-only cap mode in MCP — today a cap enforces immediately"
          minHeight={150}
        >
          <Panel className="p-4">
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="h-3 w-40 rounded bg-slate-950/10 dark:bg-white/10" />
                  <div className="h-6 w-24 rounded-full bg-slate-950/10 dark:bg-white/10" />
                </div>
              ))}
            </div>
          </Panel>
        </Blurred>
      </Card>

      {/* The catalog answers the question this page creates — "what breaks if
          I cap this?" — so it lives here rather than in a tab of its own. The
          guard panel links to it by tool anchor. */}
      <Card title="Tools & resolvers">
        <Catalog state={catalog.state} onRetry={catalog.reload} />
      </Card>
    </Page>
  );
}
